import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Trash2, Sparkles, Upload } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { demoDataService } from '../api';
import { Button } from '../components';

// Managed avatar-pool gallery (TASK-0264 Round 9). Grid of the shared demo avatars with upload / generate-more /
// delete. Cloned from EmailAssetPickerModal (grid + thumb) — this is a second call site, not yet worth a shared
// gallery abstraction.

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${p => p.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${p => p.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: 760px;
  width: 100%;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${p => p.theme.shadows?.xl || '0 25px 50px rgba(0,0,0,0.25)'};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 4px 10px;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  font-size: 12px;
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const CountLabel = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
`;

const GenPanel = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.backgroundSecondary};
  padding: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  resize: vertical;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

const GenRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
`;

const NumberInput = styled.input`
  width: 90px;
  box-sizing: border-box;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

const Select = styled.select`
  box-sizing: border-box;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  cursor: pointer;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

const HiddenInput = styled.input`
  display: none;
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: ${p => p.theme.spacing.md};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: ${p => p.theme.spacing.sm} 0;
`;

const AssetCard = styled.div`
  position: relative;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  background-color: ${p => p.theme.colors.backgroundSecondary};
`;

const AssetThumb = styled.div`
  width: 100%;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.background};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AssetInfo = styled.div`
  padding: 6px 8px;
  font-size: 10px;
  color: ${p => p.theme.colors.muted};
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  opacity: 0.85;
  &:hover { opacity: 1; background: ${p => p.theme.colors.danger || '#d9534f'}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.muted};
  font-size: 14px;
  grid-column: 1 / -1;
`;

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ALLOWED = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_BYTES = 6 * 1024 * 1024;

export const ManageAvatarPoolModal = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showGen, setShowGen] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genCount, setGenCount] = useState(6);
  const [genModel, setGenModel] = useState('');

  const loadAvatars = useCallback(async () => {
    setLoading(true);
    try {
      const items = await demoDataService.listAvatars();
      setAvatars(Array.isArray(items) ? items : []);
    } catch {
      toast.error('Failed to load avatars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadAvatars();
  }, [isOpen, loadAvatars]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error('Only PNG, JPEG, GIF or WebP images are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be under 6 MB');
      return;
    }
    setUploading(true);
    try {
      const item = await demoDataService.addAvatar(file);
      setAvatars(prev => [item, ...prev]);
      toast.success('Avatar added');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Remove this avatar from the shared pool? It affects every demo.')) return;
    setDeleting(item.name);
    try {
      await demoDataService.deleteAvatar(item.name);
      setAvatars(prev => prev.filter(a => a.name !== item.name));
      toast.success('Avatar removed');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  // Append a fresh AI batch with the chosen prompt / count / model. Completion is the background flag flipping
  // off (count is already > 0 for an existing pool, so we poll `generating`, not `ready`).
  const handleGenerate = async () => {
    const count = Math.max(1, Math.min(10, Number(genCount) || 6));
    setGenerating(true);
    try {
      const res = await demoDataService.generateAvatars({ prompt: genPrompt, count, model: genModel });
      toast.info(res?.message || 'Generating avatars in the background…');
      const deadline = Date.now() + 5 * 60 * 1000;
      const poll = async () => {
        try {
          const s = await demoDataService.getAvatarPoolStatus();
          if (!s?.generating || Date.now() > deadline) {
            await loadAvatars();
            toast.success(`Avatar pool ready (${s?.count ?? 0} avatars)`);
            setGenerating(false);
            return;
          }
        } catch {
          if (Date.now() > deadline) { setGenerating(false); return; }
        }
        setTimeout(poll, 6000);
      };
      setTimeout(poll, 6000);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to generate avatars');
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} theme={theme} onClick={onClose}>
      <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <Title theme={theme}>Avatar pool</Title>
          <CloseButton theme={theme} onClick={onClose}>X</CloseButton>
        </ModalHeader>

        <Toolbar theme={theme}>
          <HiddenInput
            type="file"
            id="avatar-pool-upload"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleUpload}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => document.getElementById('avatar-pool-upload').click()}
            disabled={uploading}
          >
            <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowGen(v => !v)} disabled={generating}>
            <Sparkles size={14} /> {generating ? 'Generating…' : 'Generate…'}
          </Button>
          <CountLabel theme={theme}>{avatars.length} avatar{avatars.length === 1 ? '' : 's'}</CountLabel>
        </Toolbar>

        {showGen && (
          <GenPanel theme={theme}>
            <div>
              <FieldLabel theme={theme}>Prompt (optional)</FieldLabel>
              <TextArea
                theme={theme}
                value={genPrompt}
                onChange={e => setGenPrompt(e.target.value)}
                placeholder="Leave blank for a varied mix, or describe the people — e.g. “field technicians in hi-vis vests, outdoors, diverse ages and ethnicities”"
              />
            </div>
            <GenRow theme={theme}>
              <div>
                <FieldLabel theme={theme}>How many</FieldLabel>
                <NumberInput
                  theme={theme}
                  type="number"
                  min={1}
                  max={10}
                  value={genCount}
                  onChange={e => setGenCount(e.target.value)}
                />
              </div>
              <div>
                <FieldLabel theme={theme}>Image model</FieldLabel>
                <Select theme={theme} value={genModel} onChange={e => setGenModel(e.target.value)}>
                  <option value="">Standard — faster, lower cost</option>
                  <option value="gpt-image-1">High quality — more natural</option>
                </Select>
              </div>
              <Button size="sm" onClick={handleGenerate} disabled={generating} style={{ marginLeft: 'auto' }}>
                <Sparkles size={14} /> {generating ? 'Generating…' : 'Generate'}
              </Button>
            </GenRow>
          </GenPanel>
        )}

        <AssetGrid theme={theme}>
          {loading ? (
            <EmptyState theme={theme}>Loading avatars…</EmptyState>
          ) : avatars.length === 0 ? (
            <EmptyState theme={theme}>
              No avatars yet. Upload your own or hit “Generate more” to create a batch with AI.
            </EmptyState>
          ) : (
            avatars.map(item => (
              <AssetCard key={item.name} theme={theme}>
                <AssetThumb theme={theme}>
                  <img src={item.url} alt={item.name} loading="lazy" />
                </AssetThumb>
                <AssetInfo theme={theme}>{formatFileSize(item.sizeBytes)}</AssetInfo>
                <DeleteBtn
                  theme={theme}
                  title="Remove avatar"
                  disabled={deleting === item.name}
                  onClick={() => handleDelete(item)}
                >
                  <Trash2 size={14} />
                </DeleteBtn>
              </AssetCard>
            ))
          )}
        </AssetGrid>
      </ModalContent>
    </Overlay>
  );
};

export default ManageAvatarPoolModal;
